import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import { supabase } from "../../supabaseClient";
import { API_URL } from "../../globals";
import axios from "axios";

const StripePaymentStep = ({ onComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [testCases, setTestCases] = useState([]);
  const [appUrl, setAppUrl] = useState("");
  const [loginNotes, setLoginNotes] = useState("");
  const [checkingPayment, setCheckingPayment] = useState(false);
  const isDevelopment = process.env.REACT_APP_ENV === 'local';

  // Novel approach: Two-phase payment system
  const [paymentPhase, setPaymentPhase] = useState('loading'); // 'loading', 'redirect', 'verify', 'complete'

  // Initialize payment phase and load data
  useEffect(() => {
    const initializePaymentStep = () => {
      // Determine the current payment phase
      const hasBeenToStripe = localStorage.getItem('stripe_payment_initiated');
      const hasPendingData = localStorage.getItem('pendingOnboardingData');
      
      if (isDevelopment) {
        setPaymentPhase('redirect'); // Show dev button
      } else if (hasBeenToStripe && hasPendingData) {
        setPaymentPhase('verify'); // User returned from Stripe, verify payment
      } else if (hasBeenToStripe && !hasPendingData) {
        setPaymentPhase('complete'); // Payment already completed, advance
      } else {
        setPaymentPhase('redirect'); // First time, redirect to Stripe
      }

      // Load onboarding data
      try {
        const storedTestCases = localStorage.getItem('onboardingTestCases');
        const storedAppUrl = localStorage.getItem('onboardingAppUrl');
        const storedLoginNotes = localStorage.getItem('onboardingLoginNotes');

        if (storedTestCases) {
          setTestCases(JSON.parse(storedTestCases));
        }
        if (storedAppUrl) {
          setAppUrl(storedAppUrl);
        }
        if (storedLoginNotes) {
          setLoginNotes(storedLoginNotes);
        }
      } catch (err) {
        console.error('Error loading onboarding data from localStorage:', err);
        setError('Failed to load onboarding data. Please go back and try again.');
      }
    };

    initializePaymentStep();
  }, [isDevelopment]);

  // Handle automatic redirect phase
  useEffect(() => {
    const handleRedirectPhase = async () => {
      if (paymentPhase === 'redirect' && !isDevelopment && testCases.length && appUrl) {
        // Small delay to show the preparing message
        setTimeout(async () => {
          await handlePayment();
        }, 1000);
      } else if (paymentPhase === 'complete') {
        // Payment already completed, advance immediately
        onComplete();
      }
    };

    handleRedirectPhase();
  }, [paymentPhase, testCases, appUrl, isDevelopment]);

  // Check subscription status for verification phase
  useEffect(() => {
    if (paymentPhase !== 'verify') return;

    let checkInterval;
    
    const checkSubscriptionStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        
        const authToken = session.access_token;
        const response = await fetch(`${API_URL}/user/check_subscription`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.subscribed) {
            // User has valid subscription, check if we have pending onboarding data
            const pendingData = localStorage.getItem('pendingOnboardingData');
            if (pendingData) {
              try {
                const { testCases: storedTestCases, appUrl: storedAppUrl, loginNotes: storedLoginNotes, timestamp } = JSON.parse(pendingData);
                
                // Check if data is not too old (within 1 hour)
                const oneHour = 60 * 60 * 1000;
                if (Date.now() - timestamp < oneHour) {
                  // Clear the interval and start project creation
                  if (checkInterval) {
                    clearInterval(checkInterval);
                  }
                  
                  setCheckingPayment(true);
                  
                  // Set the data from storage
                  setTestCases(storedTestCases);
                  setAppUrl(storedAppUrl);
                  setLoginNotes(storedLoginNotes);
                  
                  // Clean up all payment-related storage
                  localStorage.removeItem('pendingOnboardingData');
                  localStorage.removeItem('stripe_payment_initiated');
                  
                  // Create the project
                  await createProject();
                  return;
                }
              } catch (err) {
                console.error('Error parsing pending onboarding data:', err);
                localStorage.removeItem('pendingOnboardingData');
              }
            }
          }
        }
      } catch (err) {
        console.error('Error checking subscription:', err);
      }
    };

    // Check immediately and then every 3 seconds, only in verify phase
    checkSubscriptionStatus();
    checkInterval = setInterval(checkSubscriptionStatus, 3000);

    // Cleanup interval on component unmount or phase change
    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [paymentPhase]);

  const generateCheckoutURL = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const authToken = session.access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    try {
      // Set redirect URL to return to onboarding payment step
      const redirectUrl = encodeURIComponent(`${window.location.origin}/onboarding`);
      
      const response = await axios.get(
        `${API_URL}/stripe/generate_checkout_url`,
        {
          headers: headers,
          params: {
            redirect: redirectUrl,
            default_quantity: 1
          }
        }
      );
      return response.data.url;
    } catch (error) {
      console.error('Error generating checkout URL:', error);
      throw error;
    }
  };

  const createProject = async () => {
    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session.access_token;

      // Create playwright project
      const response = await axios.post(
        `${API_URL}/test/playwright_project`,
        {
          test_cases: testCases.map(tc => ({
            case: tc.case,
            page_name: tc.page_name,
            guide: tc.guide || null
          })),
          app_url: appUrl,
          login_notes: loginNotes || ""
        },
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        // Clean up localStorage data after successful submission
        localStorage.removeItem('onboardingTestCases');
        localStorage.removeItem('onboardingAppUrl');
        localStorage.removeItem('onboardingLoginNotes');
        
        onComplete();
      } else {
        setError("Failed to create project. Please try again.");
      }
    } catch (err) {
      console.error('Error creating playwright project:', err);
      setError(err.response?.data?.message || "An error occurred creating the project. Please try again.");
    }
  };

  const handlePayment = async () => {
    // Validate required data
    if (!testCases || testCases.length === 0) {
      setError("No test cases found. Please go back and complete the video processing step.");
      return;
    }

    if (!appUrl) {
      setError("App URL is required. Please go back and complete the app setup step.");
      return;
    }

    setIsLoading(true);
    setError("");
    
    try {
      // Store onboarding data with a timestamp for when user returns
      const onboardingData = {
        testCases,
        appUrl,
        loginNotes,
        timestamp: Date.now()
      };
      localStorage.setItem('pendingOnboardingData', JSON.stringify(onboardingData));
      
      // Mark that we've initiated Stripe payment
      localStorage.setItem('stripe_payment_initiated', 'true');

      // Generate checkout URL and redirect
      const checkoutUrl = await generateCheckoutURL();
      
      // Redirect to Stripe checkout
      window.location.href = checkoutUrl;
      
    } catch (err) {
      console.error('Error starting payment flow:', err);
      setError(err.response?.data?.message || "Failed to initialize payment. Please try again.");
      setIsLoading(false);
    }
  };

  const handleDevSubmit = async () => {
    // Validate required data
    if (!testCases || testCases.length === 0) {
      setError("No test cases found. Please go back and complete the video processing step.");
      return;
    }

    if (!appUrl) {
      setError("App URL is required. Please go back and complete the app setup step.");
      return;
    }

    setIsLoading(true);
    setError("");
    
    try {
      await createProject();
    } catch (err) {
      console.error('Error in dev submit:', err);
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Show payment verification state
  if (checkingPayment) {
    return (
      <Box 
        sx={{ 
          width: "100%",
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            maxWidth: "600px",
            mx: "auto",
            px: 3,
            width: "100%",
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: "#ffffff",
              mb: 3,
              letterSpacing: "-0.02em",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            Verifying Payment
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: "#94a3b8",
              fontWeight: 400,
              mb: 6,
              lineHeight: 1.6,
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            Please wait while we verify your payment and create your project...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Development mode - show only confirm button
  if (isDevelopment) {
    return (
      <Box 
        sx={{ 
          width: "100%",
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            maxWidth: "600px",
            mx: "auto",
            px: 3,
            width: "100%",
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: "#ffffff",
              mb: 3,
              letterSpacing: "-0.02em",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            Development Mode
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: "#94a3b8",
              fontWeight: 400,
              mb: 6,
              lineHeight: 1.6,
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            Payment step is bypassed in development mode.
            {testCases.length > 0 && (
              <><br />Ready to create project with {testCases.length} test cases.</>
            )}
          </Typography>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 4,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
                '& .MuiAlert-icon': {
                  color: '#ef4444'
                }
              }}
            >
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            onClick={handleDevSubmit}
            disabled={isLoading || !testCases.length || !appUrl}
            sx={{
              maxWidth: "320px",
              height: 56,
              borderRadius: 2,
              background: "#ffffff",
              color: "#000000",
              fontSize: "1.1rem",
              fontWeight: 600,
              textTransform: "none",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
              boxShadow: "0 4px 16px rgba(255, 255, 255, 0.2)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(255, 255, 255, 0.25)",
                background: "#f8fafc",
              },
              "&:disabled": {
                background: "rgba(255, 255, 255, 0.5)",
                color: "#64748b",
              }
            }}
          >
            {isLoading ? "Creating Project..." : "Continue"}
          </Button>
        </Box>
      </Box>
    );
  }

  // Render based on payment phase
  if (paymentPhase === 'loading') {
    return (
      <Box 
        sx={{ 
          width: "100%",
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
      >
        <Typography variant="h6" sx={{ color: "#94a3b8" }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (paymentPhase === 'verify' || checkingPayment) {
    return (
      <Box 
        sx={{ 
          width: "100%",
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            maxWidth: "600px",
            mx: "auto",
            px: 3,
            width: "100%",
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: "#ffffff",
              mb: 3,
              letterSpacing: "-0.02em",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            Verifying Payment
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: "#94a3b8",
              fontWeight: 400,
              mb: 6,
              lineHeight: 1.6,
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            Please wait while we verify your payment and create your project...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Production mode - show redirecting state
  return (
    <Box 
      sx={{ 
        width: "100%",
        minHeight: "70vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        py: 8,
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          maxWidth: "600px",
          mx: "auto",
          px: 3,
          width: "100%",
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            color: "#ffffff",
            mb: 3,
            letterSpacing: "-0.02em",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
          }}
        >
          {isLoading ? "Redirecting to Payment..." : "Preparing Payment"}
        </Typography>
        
        <Typography
          variant="h6"
          sx={{
            color: "#94a3b8",
            fontWeight: 400,
            mb: 6,
            lineHeight: 1.6,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
          }}
        >
          {isLoading ? (
            "You're being redirected to Stripe's secure payment page..."
          ) : (
            <>
              You'll be automatically redirected to complete your subscription.
              {testCases.length > 0 && (
                <><br />After payment, we'll create your project with {testCases.length} test cases.</>
              )}
            </>
          )}
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 4,
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
              '& .MuiAlert-icon': {
                color: '#ef4444'
              }
            }}
          >
            {error}
            <br />
            <Button 
              variant="text" 
              size="small" 
              onClick={handlePayment}
              sx={{ 
                color: '#ef4444', 
                textDecoration: 'underline',
                mt: 1,
                fontSize: '0.875rem'
              }}
            >
              Click here to retry
            </Button>
          </Alert>
        )}

        {!error && (
          <Typography
            sx={{
              color: "#64748b",
              fontSize: "0.875rem",
              mt: 4,
              lineHeight: 1.5,
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
            }}
          >
            🔒 Secure payment powered by Stripe
            <br />
            Your payment information is handled securely and we never store your card details.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default StripePaymentStep; 